import React, { useEffect, useState } from "react";
import { FormikHelpers } from "formik";
import { getIn } from "formik";
import {
  Grid,
  List,
  Card,
  Button,
  Divider,
  Checkbox,
  ListItem,
  CardHeader,
  ListItemIcon,
  ListItemText,
  TextField,
  Box,
} from "@mui/material";

import { InputMessage } from "../index";
import { not, intersection, union } from "./utils";

interface IErrorMessageComponent {
  error: string;
}

interface IOption {
  label: string;
  value: number | string;
  id: number;
}
interface IProps {
  leftItems: IOption[];
  rightItems?: IOption[];
  setFieldValue: FormikHelpers<IOption>["setFieldValue"];
  leftColumnTitle: string;
  rightColumnTitle: string;
  field: {
    name: string;
    value: IOption[];
  };
  form: {
    errors: {
      name: string;
    };
    touched: boolean;
    values: {
      [key: string]: unknown;
    };
  };
  ErrorMessageComponent: ({ error }: IErrorMessageComponent) => React.ReactElement;
}

const BuildTransferList = ({
  leftItems,
  setFieldValue,
  leftColumnTitle,
  rightColumnTitle,
  form: { errors, values },
  field,
  ErrorMessageComponent = InputMessage,
}: IProps): React.ReactElement => {
  const errorMessage = getIn(errors, field.name);
  const initialValues = getIn(values, field.name) as IOption[] | undefined;

  const isError = errorMessage;

  const [checked, setChecked] = React.useState<IOption[]>([]);
  const [left, setLeft] = React.useState<IOption[]>(
    leftItems?.filter(({ value }) => {
      return !initialValues?.some((item: IOption) => item?.value === value);
    })
  );
  const [right, setRight] = React.useState<IOption[]>(initialValues || []);

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  const handleToggle = (value: IOption) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const numberOfChecked = (items: IOption[]) => intersection(checked, items).length;

  const handleToggleAll = (items: IOption[]) => () => {
    if (numberOfChecked(items) === items.length) {
      setChecked(not(checked, items));
    } else {
      setChecked(union(checked, items));
    }
  };

  const handleCheckedRight = () => {
    setRight(right.concat(leftChecked));
    setLeft(not(left, leftChecked));
    setChecked(not(checked, leftChecked));
  };

  const handleCheckedLeft = () => {
    setLeft(left.concat(rightChecked));
    setRight(not(right, rightChecked));
    setChecked(not(checked, rightChecked));
  };

  const CustomList = ({ title, items }: { title: string; items: IOption[] }) => {
    const [search, setSearch] = useState("");
    const filteredItems = items.filter((item) =>
      item.label.toLowerCase().includes(search.toLowerCase())
    );
    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(event.target.value);
    };

    return (
      <Card>
        <CardHeader
          sx={{ padding: "1px" }}
          avatar={
            <Checkbox
              size="small"
              onClick={handleToggleAll(items)}
              checked={numberOfChecked(items) === items.length && items.length !== 0}
              indeterminate={
                numberOfChecked(items) !== items.length && numberOfChecked(items) !== 0
              }
              disabled={items.length === 0}
            />
          }
          title={title}
          subheader={`${numberOfChecked(items)}/${items.length} selected`}
        />
        <Divider sx={{ opacity: "0.6" }} />
        <Box p="2px">
          <TextField
            onChange={handleSearchChange}
            variant="standard"
            size="small"
            placeholder="Search asset"
            sx={{ paddingLeft: "10px", width: "calc(100% - 20px)" }}
          />
        </Box>

        <List sx={{ height: "230px", overflow: "auto" }} role="list">
          {filteredItems.map((value) => {
            const labelId = `transfer-list-all-item-${value.value}-label`;
            return (
              <ListItem
                key={value.id}
                role="listitem"
                onClick={handleToggle(value)}
                sx={{ padding: 0 }}
              >
                <ListItemIcon>
                  <Checkbox
                    size="small"
                    checked={checked.indexOf(value) !== -1}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText id={labelId} primary={value.label} />
              </ListItem>
            );
          })}
          <ListItem />
        </List>
      </Card>
    );
  };

  useEffect(() => {
    setFieldValue("assets", right);
  }, [right, initialValues]);

  // useEffect(() => {
  //   setRight(initialValues || []);
  // }, [initialValues]);

  return (
    <Grid container>
      <Grid item xs={5}>
        <CustomList title={leftColumnTitle} items={left} />
      </Grid>

      <Grid item xs={2}>
        <Grid container direction="column" alignItems="center">
          <Button
            variant="outlined"
            size="small"
            sx={{ margin: 0.5 }}
            onClick={handleCheckedRight}
            disabled={leftChecked.length === 0}
          >
            &gt;
          </Button>
          <Button
            variant="outlined"
            size="small"
            sx={{ margin: 0.5 }}
            onClick={handleCheckedLeft}
            disabled={rightChecked.length === 0}
          >
            &lt;
          </Button>
        </Grid>
      </Grid>

      <Grid item xs={5}>
        <CustomList title={rightColumnTitle} items={right} />
        {isError && <ErrorMessageComponent error={errorMessage} />}
      </Grid>
    </Grid>
  );
};

export default BuildTransferList;
